import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import { NoAccess, isForbidden, useApiErrorText } from '@/lib/staffGate'

import { useDeleteDoorEntry, useDoorTally, useDoorTickets, useRecordDoorEntry } from './api'
import type { DoorPaymentMethod } from './types'

/**
 * Le compteur de la porte.
 *
 * Quelqu'un se présente sans rien avoir réservé, paie, et entre. C'est le cas
 * le plus courant d'une soirée, et il n'existait nulle part : ces personnes
 * n'apparaissaient ni dans le nombre d'entrées, ni dans les recettes.
 *
 * Conçu pour un pouce, une file devant soi : on tape la formule, on ajuste la
 * quantité, on choisit liquide ou PayPal, on valide. Pas de nom, pas
 * d'adresse — demander une adresse pour encaisser vingt euros ferait tout
 * bloquer.
 *
 * Les boutons sont larges et le total s'affiche en gros : à l'entrée on lit de
 * loin, dans le bruit, et on n'a pas le temps de chercher.
 */
export function DoorCounter({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const [chosen, setChosen] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [method, setMethod] = useState<DoorPaymentMethod>('cash')

  const tickets = useDoorTickets(eventId, true)
  const tally = useDoorTally(eventId, true)
  const record = useRecordDoorEntry(eventId)
  const remove = useDeleteDoorEntry(eventId)
  const errorText = useApiErrorText()

  const rows = tickets.data ?? []
  const currency = tally.data?.currency ?? rows[0]?.currency ?? 'EUR'
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, i18n.language)

  // Un refus n'est pas une liste vide : « aucune formule » envoyait un
  // barman connecté ici chercher dans le back-office un billet qui existait.
  if (isForbidden(tickets.error)) return <NoAccess inline />

  // Aucune formule : l'équipe n'a pas encore créé de billet. Le dire vaut
  // mieux qu'un écran vide où l'on cherche le bouton.
  if (!tickets.isPending && !tickets.isError && rows.length === 0) {
    return (
      <Card className="mt-8">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('checkin.door.title')}
        </h2>
        <p className="mt-2 text-sm text-content-secondary">{t('checkin.door.noTickets')}</p>
      </Card>
    )
  }

  const selected = rows.find((row) => row.ticket_type_id === chosen) ?? null
  const people = selected ? selected.admits_count * quantity : 0
  const total = selected ? selected.price_minor * quantity : 0

  return (
    <Card className="mt-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('checkin.door.title')}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-content-secondary">
            {t('checkin.door.intro')}
          </p>
        </div>
        {tally.data ? (
          <p className="shrink-0 sm:text-right">
            <span className="block text-caption uppercase tracking-wide text-content-muted">
              {t('checkin.door.collected')}
            </span>
            <span className="font-display text-heading-md tabular-nums text-content-primary">
              {money(tally.data.total_minor)}
            </span>
            <span className="block text-caption text-content-muted">
              {t('checkin.door.split', {
                cash: money(tally.data.cash_minor),
                paypal: money(tally.data.paypal_minor),
              })}
            </span>
          </p>
        ) : null}
      </div>

      {/* La formule : de gros boutons, un par formule. Une liste déroulante se
          rate à l'aveugle quand on tient un téléphone d'une main. */}
      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <button
            key={row.ticket_type_id}
            type="button"
            onClick={() => setChosen(row.ticket_type_id)}
            className={cn(
              'rounded-lg border px-4 py-3 text-left transition',
              row.ticket_type_id === chosen
                ? 'border-champagne-400/60 bg-bg-overlay'
                : 'border-line-subtle hover:border-line',
            )}
          >
            <span className="block text-sm text-content-primary">{row.label}</span>
            <span className="mt-0.5 block text-caption text-content-muted">
              {money(row.price_minor)} ·{' '}
              {t('checkin.door.admits', { count: row.admits_count })}
            </span>
          </button>
        ))}
      </div>

      {selected ? (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                aria-label={t('checkin.door.less')}
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              >
                −
              </Button>
              <span className="w-12 text-center font-display text-heading-md tabular-nums text-content-primary">
                {quantity}
              </span>
              <Button
                variant="outline"
                size="sm"
                aria-label={t('checkin.door.more')}
                onClick={() => setQuantity((current) => Math.min(50, current + 1))}
              >
                +
              </Button>
            </div>

            <div className="flex gap-2">
              {(['cash', 'paypal'] as const).map((value) => (
                <Button
                  key={value}
                  variant={method === value ? 'champagne' : 'outline'}
                  size="sm"
                  onClick={() => setMethod(value)}
                >
                  {t(`checkin.door.method.${value}`)}
                </Button>
              ))}
            </div>
          </div>

          {/* Ce qu'on va enregistrer, en toutes lettres : à l'entrée on valide
              vite, et une erreur de quantité se voit ici ou nulle part. */}
          <p className="mt-4 text-content-primary">
            {t('checkin.door.about', { count: people, amount: money(total) })}
          </p>

          {record.error ? (
            <p className="mt-2 text-sm text-state-danger">{errorText(record.error)}</p>
          ) : null}

          <Button
            className="mt-3"
            disabled={record.isPending}
            onClick={() =>
              record.mutate(
                {
                  ticket_type_id: selected.ticket_type_id,
                  quantity,
                  payment_method: method,
                },
                { onSuccess: () => setQuantity(1) },
              )
            }
          >
            {t('checkin.door.submit')}
          </Button>
        </>
      ) : null}

      {tally.data && tally.data.entries.length > 0 ? (
        <div className="mt-6 border-t border-line-subtle pt-4">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('checkin.door.recent', { count: tally.data.people })}
          </p>
          <ul className="mt-2 grid gap-1">
            {tally.data.entries.slice(0, 8).map((entry) => (
              <li key={entry.id} className="flex items-center gap-3 py-1">
                <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                  {entry.quantity} × {entry.label} ·{' '}
                  {t(`checkin.door.method.${entry.payment_method}`)}
                </span>
                <span className="shrink-0 text-sm tabular-nums text-content-primary">
                  {money(entry.total_minor)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ id: entry.id })}
                >
                  {t('checkin.door.undo')}
                </Button>
              </li>
            ))}
          </ul>
          {/* Retirer l'entrée d'un collègue est refusé : le dire, plutôt
              que de laisser croire que le bouton ne marche pas. */}
          {remove.error ? (
            <p className="mt-2 text-sm text-state-danger">{errorText(remove.error)}</p>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}
